import { mergeEnglishGameTokens } from '@/utils/clockAbbrevMerge'

/**
 * 点分字母缩写（U.S. / e.g. / Ph.D.）：整段视为一词，禁止拆成 U . S .
 * 与 V2 is_direct_display 缩写固定展示、音标/中文槽对齐配套。
 */
export function isDottedLetterAbbrevToken (t) {
  const s = String(t ?? '').trim()
  if (!s || !s.includes('.')) return false
  return /^[A-Za-z]([A-Za-z0-9]*\.)+[A-Za-z0-9]*\.?$/.test(s)
}

/**
 * 参与 en ↔ word_en 对齐的 token（含数词 100,000，用于中文/音标映射）
 * 须含至少一个字母/数字：单独的 `-` / `'` 只是标点（如 forty - five），不当词，
 * 否则会占输入槽序号，把后续槽（如 south）吞进短语组。
 * 连写 forty-five / It's 仍算词；U.S. 等点分缩写整段算词。
 */
export function isEnglishGameWordToken (t) {
  const s = String(t ?? '').trim()
  if (!s) return false
  if (/^[A-Za-z0-9'\-]+$/.test(s) && /[A-Za-z0-9]/.test(s)) return true
  if (/^[\d,]+$/.test(s) && /\d/.test(s)) return true
  if (isDottedLetterAbbrevToken(s)) return true
  return false
}

/** 需要用户输入的 token（数词如 100,000 仅固定展示，不占输入槽） */
export function isEnglishGameInputToken (t) {
  const s = String(t ?? '').trim()
  if (!isEnglishGameWordToken(s)) return false
  if (/^[\d,]+$/.test(s) && /\d/.test(s)) return false
  return true
}

/**
 * word_en 相邻槽语义重叠时，对齐用串去掉与上一槽重复的前缀。
 * 例：came | came to a stop → 第二槽对齐为 to a stop（与题库 phonetic /tuː/,/ə/,/stɒp/ 一致）。
 * 不改卡片展示用的原始 word；整槽相同（had|had）不剥，避免误伤。
 */
export function stripOverlappingWordEnPrefix (prevWord, currentWord) {
  const prev = String(prevWord || '').trim()
  const cur = String(currentWord || '').trim()
  if (!prev || !cur) return cur
  if (prev.toLowerCase() === cur.toLowerCase()) return cur
  const prevLower = prev.toLowerCase()
  const curLower = cur.toLowerCase()
  if (!curLower.startsWith(`${prevLower} `)) return cur
  return cur.slice(prev.length).trim()
}

/**
 * 将 word_en 各卡片按与整句分词相同的规则展开为 token 序列，用于与 expectedWords 对齐。
 * expectedWords 优先来自 tokens / word_en（见 expectedWordsFromStatement），与卡片同源时可直接对齐。
 * 解决：en 前缀词未出现在 word_en（如 and ... 与 add|...）时中文提示错位。
 * 仅收录「词」token；并对相邻槽「短槽 + 以短槽开头的长槽」去重叠前缀后再分词。
 * 点分缩写（U.S.）整卡原子入表，避免拆成 U/S 后与 expectedWords「U.S.」对不齐、音标丢失。
 *
 * @param {(s: string) => string[]} tokenizeEnglish 与 ModePlay.tokenizeEnglish 一致
 * @param {{ word?: string }[]} sentenceWordCards sentenceWordCards 计算结果
 * @returns {{ rawTok: string, cardIndex: number }[]}
 */
export function buildWordEnTokenSlotMap (tokenizeEnglish, sentenceWordCards) {
  const cards = sentenceWordCards || []
  if (!cards.length || typeof tokenizeEnglish !== 'function') return []
  const map = []
  let prevRawWord = ''
  for (let ci = 0; ci < cards.length; ci++) {
    const original = String(cards[ci].word ?? '').trim()
    const raw = stripOverlappingWordEnPrefix(prevRawWord, original)
    prevRawWord = original
    if (isDottedLetterAbbrevToken(raw)) {
      map.push({ rawTok: raw, cardIndex: ci })
      continue
    }
    const toks = mergeEnglishGameTokens(tokenizeEnglish(raw))
    for (let ti = 0; ti < toks.length; ti++) {
      if (!isEnglishGameWordToken(toks[ti])) continue
      map.push({ rawTok: toks[ti], cardIndex: ci })
    }
  }
  return map
}

/**
 * 整句 token 与 word_en 卡片内 token 是否视为同一符号（英文词大小写不敏感）
 */
export function tokensEqualForWordEnAlign (a, b) {
  const s1 = String(a ?? '')
  const s2 = String(b ?? '')
  if (s1 === s2) return true
  if (/^[\d,]+$/.test(s1) && /^[\d,]+$/.test(s2)) return s1 === s2
  if (isEnglishGameWordToken(s1) && isEnglishGameWordToken(s2)) {
    return s1.toLowerCase() === s2.toLowerCase()
  }
  return false
}
